import { h } from 'preact';
import { useMemo, useState, useEffect, useRef } from 'preact/hooks';
import Panel from '../utils/panel';
import MobileMenuItem from './mobile-menu-item';
import MobileSubmenu from './mobile-submenu';
import CloseBig from '../utils/icon/close-big.svg';
import Account from '../utils/icon/account.svg';
import StoreLocator from '../utils/icon/store-locator.svg';
import ContactUs from '../utils/icon/contact-us.svg';
import CaretLeft from '../utils/icon/caret-left.svg';
import Image from '../utils/image';
import {
  megaMenuMetaobjects,
  megaMenuBannerMetaobjects,
} from '../utils/storefront/queries';
import Storefront from '../utils/storefront';
import {
  formatMegaMenus,
  formatMegaMenusBanners,
} from '../utils/storefront/format-resource';

const MobileMenu = ({ menu, menuTriggers, logo, customer, locatorUrl }) => {
  if (!menu) return null;
  const [submenuContent, setSubmenuContent] = useState(false);
  const [submobileOpened, setSubmobileOpened] = useState(false);
  const [activeSubMenu, setActiveSubMenu] = useState(false);
  const [activeSubMenuLink, setActiveSubMenuLink] = useState({
    title: null,
    url: null,
  });
  const submobileCloseTrigger = useRef(false);
  const [submenuTriggers, setSubmenuTriggers] = useState({
    openTriggerEl: null,
    closeTriggerEl: null,
  });
  const menuCloseEvent = new CustomEvent('menu:close');

  const storefront = new Storefront();
  const [megaMenus, setMegaMenus] = useState(null);
  const [megaMenusBanners, setMegaMenusBanners] = useState(null);

  useEffect(() => {
    setSubmenuTriggers({
      closeTriggerEl: submobileCloseTrigger?.current,
    });
  }, [submobileCloseTrigger]);

  useEffect(() => {
    const neededMenus = submenuContent ? submenuContent?.map((m, index) => ({
      title: m.title,
      url: m.url,
      index
    })) : [];
    const neededMenusBanners = submenuContent ? submenuContent?.filter((m) =>
      m.title.includes('#banner'),
    ) : [];

    const getMegaMenus = async () => {
      try {
        const response = await storefront.request({
          query: megaMenuMetaobjects,
          variables: {
            countryCode: window.TMW.localization.countryCode,
            languageCode: window.TMW.localization.languageCode,
          },
        });

        const formattedMegaMenus = formatMegaMenus(response, neededMenus);

        setMegaMenus(formattedMegaMenus);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.log(error);
      }
    };

    const getMegaMenusBanners = async () => {
      try {
        const response = await storefront.request({
          query: megaMenuBannerMetaobjects,
          variables: {
            countryCode: window.TMW.localization.countryCode,
            languageCode: window.TMW.localization.languageCode,
          },
        });

        const formattedMegaMenusBanners = formatMegaMenusBanners(
          response,
          neededMenusBanners,
        );

        setMegaMenusBanners(formattedMegaMenusBanners);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.log(error);
      }
    };

    getMegaMenus();
    getMegaMenusBanners();
  }, [submenuContent]);

  const submenu = useMemo(
    () => (
      <MobileSubmenu
        submenuContent={submenuContent}
        setSubmenuContent={setSubmenuContent}
        megaMenus={megaMenus}
        megaMenusBanners={megaMenusBanners}
        activeSubMenuLink={activeSubMenuLink}
      />
    ),
    [megaMenus, megaMenusBanners, activeSubMenuLink, submenuContent],
  );

  const mainPanel = useMemo(
    () => (
      <Panel
        triggers={menuTriggers}
        defaultOpen={false}
        context="menu"
        additionalClasses="!h-[100dvh] bg-neutral overflow-y-scroll max-w-screen overflow-x-hidden"
        showBackdrop={false}
      >
        <Panel
          triggers={submenuTriggers}
          defaultOpen={false}
          instantOpen={submobileOpened}
          context="submenu"
          additionalClasses={'!h-[100dvh] bg-neutral overflow-y-scroll'}
          showBackdrop={false}
        >
          <div className="flex items-center justify-between border-b border-black-30 bg-cream-grey p-5">
            <div
              className="flex items-center justify-start gap-[11px]"
              onClick={() => setSubmobileOpened(false)}
            >
              <button type="button">
                <CaretLeft />
              </button>
              <span className="font-14-bold text-black">{activeSubMenu}</span>
            </div>
            <button
              type="button"
              onClick={() => {
                document.dispatchEvent(menuCloseEvent);
                setSubmobileOpened(false);
              }}
            >
              <CloseBig />
            </button>
          </div>
          {submenu}
        </Panel>

        <div className="flex items-center justify-between px-5 py-[18px] border-b border-black-30 bg-transparent">
          <a
            className="link-unstyled min-w-[80px] logo w-[80px] relative"
            href={window.Shopify.routes.root}
          >
            <Image src={logo} />
          </a>
          <button
            type="button"
            onClick={() => {
              setSubmobileOpened(false);
              document.dispatchEvent(menuCloseEvent);
            }}
          >
            <CloseBig />
          </button>
        </div>
        <ul className="py-10 px-5">
          {menu.map((link) => (
            <MobileMenuItem
              link={link}
              key={`${link.title}-${link.url}`}
              setSubmenuContent={setSubmenuContent}
              setSubmobileOpened={setSubmobileOpened}
              setActiveSubMenu={setActiveSubMenu}
              setActiveSubMenuLink={setActiveSubMenuLink}
            />
          ))}
        </ul>
        <div className="px-5">
          <ul className="border-t border-black-30 pt-10">
            <li className="flex justify-start items-center gap-[14px] mb-6">
              <a
                href={customer?.name ? 
                  `${window.Shopify.routes.root}account` : 
                  `${window.Shopify.routes.root}account/login`}
                className=" font-14 text-black whitespace-nowrap inline-flex justify-center items-center gap-[14px]"
              >
                <Account />
                {customer?.name || window.TMW?.Strings?.header?.login}
              </a>
            </li>
            <li className="flex justify-start items-center gap-[14px] mb-6">
              <a
                href={locatorUrl || '#'}
                className="font-14 text-black whitespace-nowrap inline-flex justify-center items-center gap-[14px]"
              >
                <StoreLocator />
                <span>{window.TMW?.Strings?.header?.storeLocator}</span>
              </a>
            </li>
            <li className="flex justify-start items-center gap-[14px] mb-6">
              <ContactUs />
              <p className="font-14 text-black">{window.TMW?.Strings?.header?.contactUs}</p>
            </li>
          </ul>
        </div>
      </Panel>
    ),
    [
      menuTriggers,
      submenuTriggers,
      submobileOpened,
      activeSubMenu,
      activeSubMenuLink,
      submenu,
    ],
  );

  return mainPanel;
};

export default MobileMenu;
